import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { UserCheck, UserPlus, Mail, Search, MoreHorizontal, Shield } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  active:   'bg-emerald-100 text-emerald-700',
  disabled: 'bg-gray-100 text-gray-500',
  pending:  'bg-amber-100 text-amber-700',
};

const AdminDesigners = () => {
  const { toast } = useToast();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Placeholder designers list — replace with real API call
  const designers = [];

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast({ title: 'Email required', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/designers/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: inviteEmail, name: inviteName }),
      });
      if (res.ok) {
        toast({ title: 'Invite sent', description: `Invitation sent to ${inviteEmail}` });
        setInviteOpen(false);
        setInviteEmail('');
        setInviteName('');
      } else {
        const err = await res.json();
        toast({ title: 'Failed', description: err.detail || 'Could not send invite', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 w-full">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Designers</h1>
            <p className="text-gray-500 text-sm mt-0.5">
              Manage designer accounts and send invitations.
            </p>
          </div>
          <Button
            className="bg-violet-600 hover:bg-violet-700 gap-1.5"
            onClick={() => setInviteOpen(true)}
          >
            <UserPlus className="w-4 h-4" />
            Invite Designer
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search designers..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 bg-white border-gray-200"
          />
        </div>

        {/* Empty state */}
        {designers.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
                <UserCheck className="w-7 h-7 text-violet-500" />
              </div>
              <p className="font-semibold text-gray-900 mb-1">No designers yet</p>
              <p className="text-sm text-gray-500 mb-4">
                Invite designers to submit templates and elements to your platform.
              </p>
              <Button className="bg-violet-600 hover:bg-violet-700 gap-1.5" onClick={() => setInviteOpen(true)}>
                <UserPlus className="w-4 h-4" />
                Invite your first designer
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Designer list would go here */}
        {designers.map(d => (
          <Card key={d.id}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center font-bold text-white flex-shrink-0">
                {d.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{d.name}</p>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" /> {d.email}
                </p>
              </div>
              <Badge className={`text-xs border-0 ${STATUS_COLOR[d.status] || STATUS_COLOR.pending}`}>
                {d.status || 'pending'}
              </Badge>
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <Shield className="w-3.5 h-3.5" />
                {d.submissions_count ?? 0} submissions
              </div>
              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-gray-700">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-violet-500" />
              Invite Designer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="Designer's name"
                value={inviteName}
                onChange={e => setInviteName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="designer@example.com"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
              />
            </div>
            <p className="text-xs text-gray-500">
              The designer will receive an email with a link to set up their account.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={sending}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Mail className="w-4 h-4 mr-2" />
              {sending ? 'Sending...' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminDesigners;
